import * as React from 'react';
import { Route, RouteProps, Link } from 'react-router-dom';

import LoginPage from './LoginPage';
import Header from './Header';
import Footer from './Footer';
import auth0Client from '../Auth';
import Jumbotron from './Jumbotron';
import { User } from 'src/types/User';
import { requiredRoleForPath, userHasAccess } from 'src/security/access';
import { useTrialStatus } from 'src/hooks/useTrialStatus';


export interface Props extends RouteProps {
  component: React.ComponentType<any>;
  exact: boolean;
  path: string;
  checkingSession: boolean;
  user: User | null;

}



/*
 * PrivateRoute prompts user login if user has been fetched but not found.
 * If user is fetched succesfully, we can render the Route + component given as prop.
 */

class PrivateRouteCopy extends React.Component<Props> {
  render() {
    const {
      component: Component,
      exact,
      path,
      checkingSession,
      user
    } = this.props;


    return (
      <Route
        exact={exact}
        path={path}
        render={() => {
          if (checkingSession) {
            return <div />;
          }

          if (!auth0Client.isAuthenticated()) {
            return <LoginPage />;
          }
          // Edge case: token exists but profile not restored yet.
          // If token is valid but profile hasn't hydrated yet, avoid redirecting to login
          if (auth0Client.isAuthenticated() && !user) {
            return <div />;
          }


          const testpath: string = path as string;
          const required = requiredRoleForPath(testpath);
          const roles = (user && Array.isArray(user.roles)) ? user.roles : [];
          const missingRoles = !userHasAccess(roles, required);
          const isDashboard = testpath === '/' || testpath === '/dashboard';

          if (missingRoles) {
            const roles = user?.roles || [];
            const isPaying = roles.includes('User') || roles.includes('Admin') || roles.includes('Product2');
            const { isTrialUser, isTrialExpired, daysRemaining } = useTrialStatus();
            return (
              <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Header logout={auth0Client.signOut} user={user} />
                {isDashboard && (
                  <Jumbotron logout={auth0Client.signOut} user={user} />
                )}
                <div className="app-container" style={{ paddingTop: 20, paddingBottom: 20, flex: '1 0 auto' }}>
                  {!isPaying && !isTrialUser && (
                    <h5 style={{ margin: 0, marginBottom: 12 }}>
                      Unfortunately, it appears there is no active license associated with{' '}
                      <strong>{user?.email ?? 'your account'}</strong> for the selected module.{' '}
                      For assistance upgrading your account, contact{' '}
                      <a href="mailto:contact@ferritico.com">contact@ferritico.com</a>.
                    </h5>
                  )}

                  {!isPaying && isTrialUser && (
                    isTrialExpired ? (
                      <h5 style={{ marginTop: 0, marginBottom: 16 }}>
                        Unfortunately, the 2-week free trial associated with{' '}
                        <strong>{user?.email ?? 'your account'}</strong> has expired.{' '}
                        For assistance upgrading your account, contact{' '}
                        <a href="mailto:contact@ferritico.com">contact@ferritico.com</a>.
                      </h5>
                    ) : (
                      <>
                        <h5 style={{ marginTop: 0, marginBottom: 16 }}>
                          You are currently on a 2-week free trial with{' '}
                          <strong>{user?.email ?? 'your account'}</strong>.{' '}
                          {daysRemaining !== null && (
                            <>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining.</>
                          )}
                        </h5>
                        <div className="module-grid">
                          <Link to="/cct" className="module-card">
                            <div>
                              <h4 className="module-card__title">CCT</h4>
                              <p className="module-card__desc">Try core capabilities without a license</p>
                            </div>
                          </Link>
                          <Link to="/ttt" className="module-card">
                            <div>
                              <h4 className="module-card__title">TTT</h4>
                              <p className="module-card__desc">Run isothermal transformation calculations</p>
                            </div>
                          </Link>
                        </div>
                      </>
                    )
                  )}
                </div>                <Footer logout={auth0Client.signOut} user={user} />
              </div>
            );
          }

          return (
            <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
              <Header logout={auth0Client.signOut} user={user} />
              {isDashboard && (
                <Jumbotron logout={auth0Client.signOut} user={user} />
              )}
              <div className="app-container" style={{ paddingTop: 20, paddingBottom: 20, flex: '1 0 auto' }}>
                <Component />
              </div>
              <Footer logout={auth0Client.signOut} user={user} />
            </div>
          );
        }}
      />
    );
  }
}

export default PrivateRouteCopy;
